import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import { Modal, Button, Form, Col } from 'react-bootstrap';
import { ReadCursoDto, ReadEstagiarioEstagioDto, ReadInstituicaoEnsinoDto, ResponseEstagioDto } from '@/api.types';
import { PersonFormDto } from '@/modules/people/presentation/person-form.utils';
import { TipoLotacaoEstagioList } from '@/shared/constants/enumlist';
import { UseFieldArrayAppend, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { BotaoAcaoPerfil } from '@/modules/people/presentation/components/BotaoAcaoPerfil';
import ModalExcluir from '@/shared/components/modals/ModalExcluir';
import '@/modules/people/presentation/components/perfil-tabs-acoes.css';

interface AdicionarEstagioModalProps {
  show: boolean;
  handleHide: () => void;
  cursos: ReadCursoDto[];
  instituicoes: ReadInstituicaoEnsinoDto[];
  initialData: ReadEstagiarioEstagioDto;
  estagioIndex: number;
  appendEstagio: UseFieldArrayAppend<PersonFormDto, 'estagiario.estagios'>;
  setValue: UseFormSetValue<PersonFormDto>;
  getValues: UseFormGetValues<PersonFormDto>;
  onExcluir?: () => void;
}

type EstagioArray =
  NonNullable<PersonFormDto['estagiario']> extends { estagios: infer E }
  ? E
  : ResponseEstagioDto[];

type EstagioFormValue = EstagioArray extends Array<infer Item> ? Item : ResponseEstagioDto;

const AdicionarEstagioModal: React.FC<AdicionarEstagioModalProps> = ({
  show,
  handleHide,
  cursos,
  instituicoes,
  initialData,
  estagioIndex,
  appendEstagio,
  setValue,
  getValues,
  onExcluir,
}) => {
  const { register, handleSubmit, reset } = useForm<ResponseEstagioDto>();
  const [modalExcluirShow, setModalExcluirShow] = useState(false);

  const normalizeEstagioData = (input: ResponseEstagioDto): EstagioFormValue => ({
    ...input,
    tipo_estagio: Number(input.tipo_estagio) as EstagioFormValue['tipo_estagio'],
    curso: input.curso?.id ? { ...input.curso, id: Number(input.curso.id) } : input.curso,
    instituicao_ensino: input.instituicao_ensino?.id
      ? { ...input.instituicao_ensino, id: Number(input.instituicao_ensino.id) }
      : input.instituicao_ensino,
  }) as EstagioFormValue;

  const onSubmit = (data: ResponseEstagioDto) => {
    const normalizedData = normalizeEstagioData(data);

    if (estagioIndex >= 0) {
      const estagios = getValues('estagiario.estagios');
      const existingEstagio = estagios?.[estagioIndex] as ResponseEstagioDto | undefined;

      const updatedEstagio = {
        ...existingEstagio,
        ...normalizedData,
      } as ResponseEstagioDto;

      setValue(`estagiario.estagios.${estagioIndex}`, updatedEstagio as any, {
        shouldDirty: true,
        shouldTouch: true,
      });
    } else {
      appendEstagio(normalizedData as any, { shouldFocus: false });
    }

    handleHide();
  };

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setModalExcluirShow(false);
    onExcluir?.();
    handleHide();
  };

  useEffect(() => {
    if (show) {
      setModalExcluirShow(false);
      if (initialData) reset(initialData);
    }
  }, [show, initialData, reset]);

  return (
    <>
      <Modal show={show} onHide={handleHide} size="lg" centered>
        <Modal.Header closeButton className="py-2 perfil-modal-header">
          <Modal.Title className="fs-6">
            {estagioIndex >= 0 ? 'Editar estágio' : 'Adicionar estágio'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2">
          <div role="form">
            <Row className="g-2">
              <Form.Group as={Col} md="3">
                <label htmlFor="tipo_estagio" className="form-label mb-1 small">
                  Tipo:
                </label>
                <select
                  id="tipo_estagio"
                  className="form-select form-select-sm"
                  {...register('tipo_estagio')}
                  required
                >
                  {TipoLotacaoEstagioList.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group as={Col} md="5">
                <label htmlFor="instituicao_ensino" className="form-label mb-1 small">
                  Instituição Ensino:
                </label>
                <select
                  id="instituicao_ensino"
                  className="form-select form-select-sm"
                  {...register('instituicao_ensino.id')}
                  required
                >
                  {instituicoes.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.razao_social}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group as={Col} md="4">
                <label htmlFor="curso" className="form-label mb-1 small">
                  Curso:
                </label>
                <select
                  id="curso"
                  className="form-select form-select-sm"
                  {...register('curso.id')}
                  required
                >
                  {cursos.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.nome}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group as={Col} md="3">
                <label htmlFor="data_inicio_estagio" className="form-label mb-1 small">
                  Início do Estágio:
                </label>
                <input
                  type="date"
                  id="data_inicio_estagio"
                  className="form-control form-control-sm"
                  {...register('data_inicio_estagio')}
                  required
                />
              </Form.Group>
              <Form.Group as={Col} md="3">
                <label htmlFor="data_fim_estagio" className="form-label mb-1 small">
                  Fim do Estágio:
                </label>
                <input
                  type="date"
                  id="data_fim_estagio"
                  className="form-control form-control-sm"
                  {...register('data_fim_estagio')}
                />
              </Form.Group>
              <Form.Group as={Col} md="3">
                <label htmlFor="desligamento" className="form-label mb-1 small">
                  Desligamento:
                </label>
                <input
                  type="date"
                  id="desligamento"
                  className="form-control form-control-sm"
                  {...register('desligamento')}
                />
              </Form.Group>
              <Form.Group as={Col} md="3">
                <label htmlFor="data_colacao_grau" className="form-label mb-1 small">
                  Colação de Grau:
                </label>
                <input
                  type="month"
                  id="data_colacao_grau"
                  className="form-control form-control-sm"
                  {...register('data_colacao_grau')}
                  required
                />
              </Form.Group>
              <Form.Group as={Col} md="5" className="d-flex align-items-end">
                <div className="form-check mb-1" title="Indica se o estagiário trabalha nos fins de semana">
                  <input
                    id="fim_semana"
                    className="form-check-input"
                    title="Fim de Semana"
                    type="checkbox"
                    {...register('fim_semana')}
                  />
                  <label htmlFor="fim_semana" className="form-check-label">
                    Trabalha Final de Semana
                  </label>
                </div>
              </Form.Group>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer className="py-2 d-flex justify-content-between">
          <div>
            {estagioIndex >= 0 && onExcluir && (
              <BotaoAcaoPerfil tipo="excluir" onClick={() => setModalExcluirShow(true)} />
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" type="button" onClick={handleHide}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="perfil-modal-btn-salvar"
              onClick={handleSubmit(onSubmit)}
            >
              Salvar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <ModalExcluir
        show={modalExcluirShow}
        handleHide={() => setModalExcluirShow(false)}
        handleExcluir={handleExcluir}
        mensagemPersonalizada="Deseja remover este estágio?"
      />
    </>
  );
};

export default AdicionarEstagioModal;
